// src/utils/imageOptimizer.ts
const TIFF_TYPES = ["image/tiff", "image/tif"];
const isTiff = (file: File) =>
  TIFF_TYPES.includes(file.type) ||
  /\.tiff?$/i.test(file.name);

/** Decode a TIFF file to an ImageData via utif, then draw on canvas. */
const tiffToCanvas = async (
  file: File,
  maxWidth: number,
): Promise<HTMLCanvasElement> => {
  // utif (~130KB) only runs for the rare TIFF upload — load it on demand so it
  // stays out of every chunk that touches the media API.
  const { default: UTIF } = await import("utif");
  const buffer = await file.arrayBuffer();
  const ifds = UTIF.decode(buffer);
  if (!ifds.length) throw new Error("Could not decode TIFF: no image data found.");

  // Pick first IFD (page 0)
  UTIF.decodeImage(buffer, ifds[0]);
  const ifd = ifds[0];
  const rgba = UTIF.toRGBA8(ifd);

  const srcW = ifd.width as number;
  const srcH = ifd.height as number;

  let dstW = srcW;
  let dstH = srcH;
  if (dstW > maxWidth) {
    dstH = Math.round((dstH * maxWidth) / dstW);
    dstW = maxWidth;
  }

  // Draw original size first, then scale down
  const srcCanvas = document.createElement("canvas");
  srcCanvas.width = srcW;
  srcCanvas.height = srcH;
  const srcCtx = srcCanvas.getContext("2d")!;
  const imageData = new ImageData(new Uint8ClampedArray(rgba), srcW, srcH);
  srcCtx.putImageData(imageData, 0, 0);

  if (dstW === srcW && dstH === srcH) return srcCanvas;

  const dstCanvas = document.createElement("canvas");
  dstCanvas.width = dstW;
  dstCanvas.height = dstH;
  const dstCtx = dstCanvas.getContext("2d")!;
  dstCtx.imageSmoothingEnabled = true;
  dstCtx.imageSmoothingQuality = "high";
  dstCtx.drawImage(srcCanvas, 0, 0, dstW, dstH);
  return dstCanvas;
};

const canvasToWebPFile = (
  canvas: HTMLCanvasElement,
  name: string,
  quality: number,
  maxWidth: number,
): Promise<File> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Blob conversion failed"));
        const baseName = name.replace(/\.[^.]+$/, "");
        const newName = `${baseName}_360${maxWidth < 1000 ? "_thumb" : ""}.webp`;
        resolve(new File([blob], newName, { type: "image/webp" }));
      },
      "image/webp",
      quality,
    );
  });

export const convertToWebP = async (
  file: File,
  quality = 0.8,
  maxWidth = 4096,
): Promise<File> => {
  if (isTiff(file)) {
    const canvas = await tiffToCanvas(file, maxWidth);
    return canvasToWebPFile(canvas, file.name, quality, maxWidth);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(
        new Error(
          `Format "${file.name.split(".").pop()?.toUpperCase()}" not supported. Please use JPG, PNG, WebP, or TIFF.`,
        ),
      );
    };

    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      let width = img.naturalWidth;
      let height = img.naturalHeight;

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas context failed"));

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, width, height);

      canvasToWebPFile(canvas, file.name, quality, maxWidth).then(resolve).catch(reject);
    };

    img.src = url;
  });
};
